import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getCustomers, getProducts, getNextOrderNumber, createOrder, updateOrder } from '../../services/api';
import { Plus, ArrowLeft, Minus, Package, Camera, Check, Image, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function OrderForm({ onSaved, onCancel, editOrder }) {
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [allVariants, setAllVariants] = useState([]);
    const [customerId, setCustomerId] = useState(editOrder?.customer_id || '');
    const [customerName, setCustomerName] = useState(editOrder?.customer_name || '');
    const [customerPriceGroup, setCustomerPriceGroup] = useState('');
    const [orderDate, setOrderDate] = useState(editOrder?.order_date || new Date().toISOString().split('T')[0]);
    const [orderNumber, setOrderNumber] = useState(editOrder?.order_number || '');
    const [lines, setLines] = useState(() => (editOrder?.lines || []).map(l => ({ ...l, _key: Date.now() + Math.random(), packs: l.pack_size ? Math.ceil((l.quantity || 0) / l.pack_size) : 0, _isPallet: l.is_pallet || false })));
    const [globalDiscount, setGlobalDiscount] = useState(editOrder?.global_discount || 0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [customerSearch, setCustomerSearch] = useState(editOrder?.customer_name || '');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [saving, setSaving] = useState(false);
    const [palletEnabled, setPalletEnabled] = useState(false);
    const [palletPrice, setPalletPrice] = useState('');
    const palletAlreadyAdded = lines.some(l => l._isPallet);
    const [showProfit, setShowProfit] = useState(editOrder?.show_profit ?? false);
    const [variantPicker, setVariantPicker] = useState(null);
    const [variantQtys, setVariantQtys] = useState({});
    const [showScanner, setShowScanner] = useState(false);
    const [scannerReady, setScannerReady] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [orderNotes, setOrderNotes] = useState(editOrder?.notes || '');
    const isEditing = !!editOrder?.id;
    const searchRef = useRef(null);
    const scannerRef = useRef(null);
    const customerDropdownRef = useRef(null);

    useEffect(() => {
        Promise.all([getCustomers(), getProducts(), getNextOrderNumber()])
            .then(([c, p, n]) => {
                setCustomers(c.data);
                setProducts(p.data);
                if (!editOrder?.order_number) setOrderNumber(n.data.next_number);
                const variants = [];
                for (const prod of p.data) {
                    if (prod.variants?.length) {
                        for (const v of prod.variants) variants.push({ ...v, product_id: prod.id, product_name: prod.name, _product: prod });
                    }
                }
                setAllVariants(variants);
                if (editOrder?.customer_id) {
                    const ec = c.data.find(x => x.id === editOrder.customer_id);
                    if (ec) setCustomerPriceGroup(ec.price_group || 'A');
                }
            })
            .catch(() => toast.error('Veriler yuklenemedi'));
    }, []);

    useEffect(() => {
        const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    useEffect(() => {
        const handler = (e) => { if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) setShowCustomerDropdown(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filteredCustomers = useMemo(() => {
        if (!customerSearch) return customers;
        const q = customerSearch.toLowerCase();
        return customers.filter(c => c.name.toLowerCase().includes(q) || c.tax_number?.toLowerCase().includes(q));
    }, [customers, customerSearch]);

    const selectCustomer = (c) => {
        setCustomerId(c.id);
        setCustomerName(c.name);
        setCustomerSearch(c.name);
        setShowCustomerDropdown(false);
        const pg = c.price_group || 'A';
        setCustomerPriceGroup(pg);
        setIsDirty(true);
        if (lines.length > 0) {
            setLines(prev => prev.map(l => {
                const prod = products.find(p => p.id === l.product_id);
                if (!prod) return l;
                const newPrice = getPriceForProduct(prod, pg);
                if (l.is_pack_based || l.pack_size) {
                    const baseUnit = prod.pack_content > 0 ? Math.round((newPrice / prod.pack_content) * 10000) / 10000 : 0;
                    const packPrice = Math.round(baseUnit * (l.pack_size || prod.pack_content) * 100) / 100;
                    const lt = (l.packs || 0) * packPrice * (1 - (l.discount_percent || 0) / 100);
                    return { ...l, base_unit_price: baseUnit, unit_price: packPrice, line_total: lt };
                } else {
                    const lt = l.total_pieces * newPrice * (1 - (l.discount_percent || 0) / 100);
                    return { ...l, unit_price: newPrice, line_total: lt };
                }
            }));
        }
    };

    const getPriceForProduct = (product, priceGroup) => {
        const g = (priceGroup || 'A').toLowerCase();
        return product[`price_${g}`] || product.price_a || product.price || 0;
    };

    useEffect(() => {
        if (!searchQuery || searchQuery.length < 1) { setSearchResults([]); return; }
        if (!customerId) return;
        const q = searchQuery.toLowerCase();
        const variantMatch = allVariants.find(v => v.barcode?.toLowerCase() === q);
        if (variantMatch) { addProduct(variantMatch._product, variantMatch); setSearchQuery(''); setSearchResults([]); return; }
        const barcodeMatch = products.find(p => p.barcode?.toLowerCase() === q);
        if (barcodeMatch) { handleProductClick(barcodeMatch); setSearchQuery(''); setSearchResults([]); return; }
        const refMatch = products.find(p => p.reference?.toLowerCase() === q);
        if (refMatch) { handleProductClick(refMatch); setSearchQuery(''); setSearchResults([]); return; }
        const res = products.filter(p => !p.is_deleted && (
            p.name.toLowerCase().includes(q) || p.reference?.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q)
        )).slice(0, 8);
        setSearchResults(res);
    }, [searchQuery, products, allVariants, customerId]);

    useEffect(() => {
        if (showScanner && !scannerRef.current) {
            const { Html5Qrcode } = require('html5-qrcode');
            const scanner = new Html5Qrcode("order-scanner-container");
            scannerRef.current = scanner;
            scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 150 } },
                (text) => handleBarcodeScanned(text), () => {}
            ).then(() => setScannerReady(true)).catch(() => { toast.error('Kamera acilamadi'); closeScanner(); });
        }
        return () => { if (scannerRef.current?.isScanning) scannerRef.current.stop().catch(() => {}); };
    }, [showScanner]);

    const handleBarcodeScanned = (barcode) => {
        if (!customerId) { toast.error('Once musteri secin'); closeScanner(); return; }
        const bc = barcode.toLowerCase();
        const variantMatch = allVariants.find(v => v.barcode?.toLowerCase() === bc);
        if (variantMatch) { addProduct(variantMatch._product, variantMatch); toast.success(`${variantMatch.name} eklendi`); closeScanner(); return; }
        const product = products.find(p => p.barcode?.toLowerCase() === bc);
        if (product) { handleProductClick(product); toast.success(`${product.name}`); }
        else { setSearchQuery(barcode); toast.info(`Barkod: ${barcode}`); }
        closeScanner();
    };

    const closeScanner = () => {
        if (scannerRef.current?.isScanning) scannerRef.current.stop().then(() => { scannerRef.current = null; setScannerReady(false); setShowScanner(false); }).catch(() => { scannerRef.current = null; setScannerReady(false); setShowScanner(false); });
        else { scannerRef.current = null; setScannerReady(false); setShowScanner(false); }
    };

    const openScanner = () => {
        if (!customerId) { toast.error('Once musteri secin'); return; }
        setShowScanner(true);
    };

    const addProduct = (product, variant = null) => {
        const isPackBased = product.is_pack_based && product.pack_content > 0;
        const rawPrice = getPriceForProduct(product, customerPriceGroup);
        const w = product.weight || 0;
        if (isPackBased) {
            const pc = product.pack_content;
            const baseUnit = Math.round((rawPrice / pc) * 10000) / 10000;
            setLines(prev => [...prev, {
                _key: Date.now() + Math.random(), product_id: product.id, product_name: product.name,
                reference: product.reference || '', variant_id: variant?.id || null, variant_name: variant?.name || null,
                image_url: product.image_url || null, current_stock: variant?.current_stock ?? product.current_stock ?? 0,
                is_pack_based: true, packs: 1, pack_size: pc, original_pack_size: pc,
                base_unit_price: baseUnit, unit_price: rawPrice,
                quantity: pc, total_pieces: pc, boxes_per_pallet: product.boxes_per_pallet || null,
                purchase_price: product.purchase_price || 0, discount_percent: 0,
                line_total: rawPrice, weight: w, total_weight: w * pc,
            }]);
        } else {
            setLines(prev => [...prev, {
                _key: Date.now() + Math.random(), product_id: product.id, product_name: product.name,
                reference: product.reference || '', variant_id: variant?.id || null, variant_name: variant?.name || null,
                image_url: product.image_url || null, current_stock: variant?.current_stock ?? product.current_stock ?? 0,
                is_pack_based: false, packs: 0, pack_size: null, original_pack_size: null,
                base_unit_price: null, unit_price: rawPrice,
                quantity: 1, total_pieces: 1, boxes_per_pallet: product.boxes_per_pallet || null,
                purchase_price: product.purchase_price || 0, discount_percent: 0,
                line_total: rawPrice, weight: w, total_weight: w,
            }]);
        }
        setSearchQuery(''); setSearchResults([]);
        setIsDirty(true);
        searchRef.current?.focus();
    };

    const handleProductClick = (product) => {
        if (product.variants?.length === 1) {
            addProduct(product, product.variants[0]);
        } else if (product.variants?.length > 1) {
            const qtys = {};
            product.variants.forEach(v => { qtys[v.id] = 0; });
            setVariantQtys(qtys);
            setVariantPicker(product);
            setSearchQuery(''); setSearchResults([]);
        } else {
            addProduct(product);
        }
    };

    const confirmVariantPicker = () => {
        if (!variantPicker) return;
        const toAdd = Object.entries(variantQtys).filter(([, qty]) => qty > 0);
        if (!toAdd.length) { toast.error('En az 1 varyanta miktar girin'); return; }
        toAdd.forEach(([vid, qty]) => {
            const v = variantPicker.variants.find(vv => vv.id === vid);
            if (v) {
                const isPackBased = variantPicker.is_pack_based && variantPicker.pack_content > 0;
                const rawPrice = getPriceForProduct(variantPicker, customerPriceGroup);
                const w = variantPicker.weight || 0;
                if (isPackBased) {
                    const pc = variantPicker.pack_content;
                    const baseUnit = Math.round((rawPrice / pc) * 10000) / 10000;
                    const totalItems = qty * pc;
                    setLines(prev => [...prev, {
                        _key: Date.now() + Math.random(), product_id: variantPicker.id, product_name: variantPicker.name,
                        reference: variantPicker.reference || '', variant_id: v.id, variant_name: v.name,
                        image_url: variantPicker.image_url || null, current_stock: v.current_stock ?? 0,
                        is_pack_based: true, packs: qty, pack_size: pc, original_pack_size: pc,
                        base_unit_price: baseUnit, unit_price: rawPrice,
                        quantity: totalItems, total_pieces: totalItems, boxes_per_pallet: variantPicker.boxes_per_pallet || null,
                        purchase_price: variantPicker.purchase_price || 0, discount_percent: 0,
                        line_total: qty * rawPrice, weight: w, total_weight: w * totalItems,
                    }]);
                } else {
                    setLines(prev => [...prev, {
                        _key: Date.now() + Math.random(), product_id: variantPicker.id, product_name: variantPicker.name,
                        reference: variantPicker.reference || '', variant_id: v.id, variant_name: v.name,
                        image_url: variantPicker.image_url || null, current_stock: v.current_stock ?? 0,
                        is_pack_based: false, packs: 0, pack_size: null, original_pack_size: null,
                        base_unit_price: null, unit_price: rawPrice,
                        quantity: qty, total_pieces: qty, boxes_per_pallet: variantPicker.boxes_per_pallet || null,
                        purchase_price: variantPicker.purchase_price || 0, discount_percent: 0,
                        line_total: qty * rawPrice, weight: w, total_weight: w * qty,
                    }]);
                }
            }
        });
        setVariantPicker(null);
        setVariantQtys({});
        searchRef.current?.focus();
    };

    const updateLine = (idx, field, value) => {
        setIsDirty(true);
        setLines(prev => prev.map((l, i) => {
            if (i !== idx) return l;
            const updated = { ...l, [field]: value };
            if (l.is_pack_based || l.pack_size) {
                if (field === 'packs') {
                    const p = parseInt(value) || 0;
                    updated.packs = p;
                    updated.quantity = p * (l.pack_size || 1);
                    updated.total_pieces = updated.quantity;
                    updated.line_total = p * (parseFloat(updated.unit_price) || 0) * (1 - (updated.discount_percent || 0) / 100);
                    updated.total_weight = (updated.weight || 0) * updated.quantity;
                }
                if (field === 'pack_size') {
                    const newPs = parseInt(value) || 1;
                    updated.pack_size = newPs;
                    const newPackPrice = Math.round((l.base_unit_price || 0) * newPs * 100) / 100;
                    updated.unit_price = newPackPrice;
                    updated.quantity = (l.packs || 0) * newPs;
                    updated.total_pieces = updated.quantity;
                    updated.line_total = (l.packs || 0) * newPackPrice * (1 - (updated.discount_percent || 0) / 100);
                    updated.total_weight = (updated.weight || 0) * updated.quantity;
                }
                if (field === 'content_total') {
                    const newTotal = parseInt(value) || 0;
                    const ps = l.original_pack_size || l.pack_size || 1;
                    if (newTotal <= 0) { updated.packs = 0; updated.quantity = 0; }
                    else { updated.packs = Math.ceil(newTotal / ps); updated.quantity = newTotal; }
                    updated.total_pieces = updated.quantity;
                    updated.line_total = updated.packs * (parseFloat(updated.unit_price) || 0) * (1 - (updated.discount_percent || 0) / 100);
                    updated.total_weight = (updated.weight || 0) * updated.quantity;
                }
                if (field === 'unit_price') {
                    const newPrice = parseFloat(value) || 0;
                    updated.unit_price = newPrice;
                    if (updated.pack_size) updated.base_unit_price = newPrice / updated.pack_size;
                    updated.line_total = (l.packs || 0) * newPrice * (1 - (updated.discount_percent || 0) / 100);
                }
                if (field === 'discount_percent') {
                    updated.line_total = (l.packs || 0) * (parseFloat(updated.unit_price) || 0) * (1 - (parseFloat(value) || 0) / 100);
                }
            } else {
                if (field === 'quantity') {
                    const q = parseInt(value) || 0;
                    updated.quantity = q;
                    updated.total_pieces = q;
                    updated.line_total = q * (parseFloat(updated.unit_price) || 0) * (1 - (updated.discount_percent || 0) / 100);
                    updated.total_weight = (updated.weight || 0) * q;
                }
                if (field === 'unit_price' || field === 'discount_percent') {
                    updated.line_total = updated.total_pieces * (parseFloat(updated.unit_price) || 0) * (1 - (parseFloat(updated.discount_percent) || 0) / 100);
                }
            }
            return updated;
        }));
    };

    const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

    const totals = useMemo(() => {
        const productLines = lines.filter(l => !l._isPallet);
        const subtotal = lines.reduce((s, l) => s + (l.line_total || 0), 0);
        const lineDiscountTotal = lines.reduce((s, l) => {
            const full = l.is_pack_based ? (l.packs || 0) * (parseFloat(l.unit_price) || 0) : l.total_pieces * (parseFloat(l.unit_price) || 0);
            return s + (full - (l.line_total || 0));
        }, 0);
        const globalDiscountAmount = subtotal * (parseFloat(globalDiscount) || 0) / 100;
        const total = subtotal - globalDiscountAmount;
        const cost = lines.reduce((s, l) => s + (parseFloat(l.purchase_price) || 0) * l.total_pieces, 0);
        const hasPurchasePrice = productLines.length > 0 && productLines.every(l => parseFloat(l.purchase_price) > 0);
        const profit = total - cost;
        const profitPercent = cost > 0 ? ((profit / cost) * 100) : 0;
        const lineCount = productLines.length;
        const sumPieces = productLines.reduce((s, l) => s + (parseInt(l.quantity) || 0), 0);
        const sumPacks = productLines.reduce((s, l) => s + (parseInt(l.packs) || 0), 0);
        const hasPackProducts = productLines.some(l => l.pack_size);
        const packContentTotal = productLines.filter(l => l.pack_size).reduce((s, l) => s + (parseInt(l.quantity) || 0), 0);
        const totalWeight = lines.reduce((s, l) => s + (parseFloat(l.total_weight) || 0), 0);
        let totalPallets = 0;
        productLines.forEach(l => {
            if (l.boxes_per_pallet && l.boxes_per_pallet > 0) {
                totalPallets += Math.ceil((parseInt(l.quantity) || 0) / l.boxes_per_pallet);
            }
        });
        return { subtotal, lineDiscountTotal, discountAmount: globalDiscountAmount, total, profit, profitPercent, hasPurchasePrice, lineCount, sumPieces, sumPacks, hasPackProducts, packContentTotal, totalWeight, totalPallets };
    }, [lines, globalDiscount]);

    const addPalletLine = () => {
        if (!totals.totalPallets) { toast.error('Palet hesaplanacak urun yok'); return; }
        const price = parseFloat(palletPrice) || 0;
        setLines(prev => [...prev, {
            _key: Date.now() + Math.random(), _isPallet: true, product_id: null,
            product_name: 'Palet', reference: 'PALET', variant_id: null, variant_name: null,
            image_url: null, current_stock: 0, quantity: totals.totalPallets, pack_size: null,
            packs: 0, boxes_per_pallet: null, total_pieces: totals.totalPallets,
            unit_price: price, purchase_price: 0, discount_percent: 0,
            line_total: totals.totalPallets * price, weight: 0, total_weight: 0,
        }]);
        setPalletEnabled(false);
        setPalletPrice('');
        setIsDirty(true);
        toast.success(`${totals.totalPallets} palet siparise eklendi`);
    };

    const handleBack = () => {
        if (isDirty && lines.length > 0) {
            if (window.confirm('Kaydedilmemis degisiklikler var. Kaydetmeden cikmak istediginize emin misiniz?')) onCancel();
        } else onCancel();
    };

    const fmtDec = (v) => (v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleSave = async () => {
        if (!customerId) { toast.error('Musteri secin'); return; }
        if (!lines.length) { toast.error('Urun ekleyin'); return; }
        setSaving(true);
        try {
            const payload = {
                customer_id: customerId, customer_name: customerName,
                order_date: orderDate, status: 'saved',
                global_discount: parseFloat(globalDiscount) || 0,
                lines: lines.map(l => ({
                    product_id: l.product_id, product_name: l.product_name, reference: l.reference || '',
                    variant_id: l.variant_id || null, variant_name: l.variant_name || null,
                    quantity: parseInt(l.quantity) || 0, pack_size: l.pack_size ? parseInt(l.pack_size) : null,
                    packs: parseInt(l.packs) || 0, boxes_per_pallet: l.boxes_per_pallet || null,
                    is_pallet: l._isPallet || false, is_pack_based: l.is_pack_based || false,
                    base_unit_price: l.base_unit_price || null, original_pack_size: l.original_pack_size || null,
                    total_pieces: parseInt(l.total_pieces) || 0,
                    unit_price: parseFloat(l.unit_price) || 0, purchase_price: parseFloat(l.purchase_price) || 0,
                    discount_percent: parseFloat(l.discount_percent) || 0,
                    line_total: parseFloat(l.line_total) || 0,
                    weight: parseFloat(l.weight) || 0, total_weight: parseFloat(l.total_weight) || 0,
                })),
                total: totals.total, subtotal: totals.subtotal, discount_amount: totals.discountAmount,
                profit: totals.profit, line_count: totals.lineCount, sum_pieces: totals.sumPieces,
                total_pallets: totals.totalPallets, show_profit: showProfit,
                notes: orderNotes || null,
            };
            if (isEditing) await updateOrder(editOrder.id, payload);
            else await createOrder(payload);
            toast.success(isEditing ? 'Siparis guncellendi' : 'Siparis kaydedildi');
            setIsDirty(false);
            onSaved();
        } catch (err) { toast.error(err.response?.data?.detail || 'Kaydetme hatasi'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-5 max-w-7xl mx-auto" data-testid="order-form-page">
            <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1D1D1F] transition-colors" data-testid="back-to-orders-btn">
                <ArrowLeft size={16} /> Geri
            </button>

            {/* HEADER BAR */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="order-header">
                <div className="flex flex-wrap items-center gap-4">
                    <span className="inline-flex items-center px-3.5 py-1.5 rounded-lg bg-[#ECFDF5] text-[#059669] text-xs font-bold tracking-wide" data-testid="order-badge">{isEditing ? 'Duzenle' : 'Siparis'}</span>
                    <input type="text" value={orderNumber} readOnly className="order-input w-[120px] font-mono font-semibold text-[#1D1D1F]" data-testid="order-number-badge" />
                    <input type="date" value={orderDate} onChange={e => { setOrderDate(e.target.value); setIsDirty(true); }} className="order-input w-[140px] text-sm text-[#111827]" data-testid="order-date-input" />
                    <div className="relative flex-1 min-w-[180px]" ref={customerDropdownRef}>
                        <input type="text" value={customerSearch} onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); if (!e.target.value) { setCustomerId(''); setCustomerName(''); setCustomerPriceGroup(''); } }}
                            onFocus={() => setShowCustomerDropdown(true)} placeholder="Musteri ara..." className="order-input w-full" data-testid="order-customer-select" />
                        {showCustomerDropdown && filteredCustomers.length > 0 && (
                            <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="customer-dropdown">
                                {filteredCustomers.map(c => (
                                    <button key={c.id} onClick={() => selectCustomer(c)} className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] text-sm transition-colors flex items-center justify-between border-b border-[#F3F4F6] last:border-0">
                                        <span className="font-medium text-[#111827]">{c.name}</span>
                                        {c.price_group && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ECFDF5] text-[#059669] font-bold">{c.price_group}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div className={`relative rounded-2xl border p-4 transition-colors ${customerId ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#F9FAFB] border-[#E5E7EB]'}`}>
                {!customerId && (
                    <div className="absolute inset-0 z-10 rounded-2xl flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                        <span className="text-sm font-medium text-[#9CA3AF]">Once musteri secin</span>
                    </div>
                )}
                <div className="flex items-center gap-3">
                    <button type="button" onClick={openScanner} disabled={!customerId}
                        className="w-10 h-10 rounded-xl bg-white border border-[#D1FAE5] flex items-center justify-center flex-shrink-0 hover:bg-[#ECFDF5] transition-colors disabled:opacity-40" data-testid="order-scanner-btn">
                        <Camera size={18} className="text-[#059669]" />
                    </button>
                    <div className="relative flex-1">
                        <input ref={searchRef} type="text" value={searchQuery}
                            onChange={e => { if (!customerId) { toast.error('Once musteri secin'); return; } setSearchQuery(e.target.value); }}
                            placeholder="Barkod veya urun adi..." disabled={!customerId}
                            className="w-full h-11 px-4 bg-white border border-[#D1D5DB] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed" data-testid="order-product-search" />
                    </div>
                    <button type="button" onClick={() => { if (!customerId) toast.error('Once musteri secin'); else searchRef.current?.focus(); }} disabled={!customerId}
                        className="h-11 px-5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="order-add-btn">
                        <Plus size={16} /> Ekle
                    </button>
                </div>
                {searchResults.length > 0 && customerId && (
                    <div className="absolute z-20 left-4 right-4 mt-2 bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="order-search-results">
                        {searchResults.map((p, i) => (
                            <button key={p.id + '-' + i} onClick={() => handleProductClick(p)}
                                className="w-full text-left px-4 py-3 hover:bg-[#F9FAFB] flex items-center gap-3 border-b border-[#F3F4F6] last:border-0 transition-colors">
                                <div className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                                    {p.image_url ? <img src={p.image_url} alt="" className="w-9 h-9 rounded-lg object-cover" /> : <Image size={14} className="text-[#9CA3AF]" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-[#111827] truncate">{p.name}</div>
                                    <div className="text-[11px] text-[#9CA3AF]">
                                        {p.reference || ''} {p.barcode ? `\u00B7 ${p.barcode}` : ''}
                                        {p.variants?.length > 1 && <span className="text-[#6366F1] ml-1">({p.variants.length} varyant)</span>}
                                        {p.is_pack_based && <span className="text-[#F59E0B] ml-1">(paket: {p.pack_content})</span>}
                                    </div>
                                </div>
                                <span className="text-xs font-bold text-[#3B82F6]">{fmtDec(getPriceForProduct(p, customerPriceGroup))}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* ORDER LINES TABLE */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
                {(() => {
                    const hasPackBased = lines.some(l => l.pack_size);
                    const hasDiscount = lines.some(l => parseFloat(l.discount_percent) > 0);
                    return (
                <table className="w-full" data-testid="order-lines-table">
                    <thead>
                        <tr className="border-b border-[#E5E7EB]">
                            <th className="text-left px-4 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: hasPackBased ? '28%' : '36%' }}>Urun</th>
                            <th className="text-center px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '9%' }}>Varyant</th>
                            {hasPackBased ? (
                                <>
                                    <th className="text-center px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '9%' }}>Paket</th>
                                    <th className="text-center px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '9%' }}>Icerik</th>
                                </>
                            ) : (
                                <th className="text-center px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '11%' }}>Adet</th>
                            )}
                            <th className="text-center px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '11%' }}>Fiyat</th>
                            <th className="text-center px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '7%' }}>Isk%</th>
                            {hasDiscount && <th className="text-right px-2 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '9%' }}>Ind.</th>}
                            <th className="text-right px-4 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider" style={{ width: '11%' }}>Toplam</th>
                            <th style={{ width: '36px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.length === 0 ? (
                            <tr><td colSpan={hasPackBased ? (hasDiscount ? 9 : 8) : (hasDiscount ? 8 : 7)} className="text-center py-16 text-[#9CA3AF]">
                                <Package size={28} className="mx-auto mb-2 opacity-40" />
                                <span className="text-sm">Urun eklemek icin arama kutusunu kullanin</span>
                            </td></tr>
                        ) : lines.map((l, idx) => {
                            const fullPrice = l.quantity * (parseFloat(l.unit_price) || 0);
                            const discountAmt = fullPrice - (l.line_total || 0);
                            return (
                            <tr key={l._key} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors" data-testid={`order-line-${idx}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-md bg-[#F3F4F6] border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
                                            {l.image_url ? <img src={l.image_url} alt="" className="w-10 h-10 rounded-md object-cover" /> : <Image size={14} className="text-[#C0C0C0]" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[11px] text-[#9CA3AF] leading-none mb-0.5">{l.reference || '\u2014'}</div>
                                            <div className="text-sm font-bold text-[#111827] truncate">{l.product_name}</div>
                                            <div className="text-[11px] leading-none mt-0.5">
                                                {l.current_stock > 0 ? <span className="text-[#059669]">Stok: {l.current_stock}</span> : <span className="text-[#EF4444]">Stok yok</span>}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-2 py-3 text-center text-sm text-[#9CA3AF]">{l.variant_name || '-'}</td>
                                {hasPackBased ? (
                                    <>
                                        <td className="px-2 py-3">
                                            <div className="flex justify-center">
                                                <input type="number" value={l.pack_size ? (l.packs || '') : (l.quantity || '')} onChange={e => {
                                                    const val = e.target.value === '' ? 0 : parseInt(e.target.value);
                                                    l.pack_size ? updateLine(idx, 'packs', val) : updateLine(idx, 'quantity', val);
                                                }} min="0"
                                                    className="w-14 h-8 text-center text-sm font-semibold border border-[#E5E7EB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{borderRadius:'4px'}} data-testid={l.pack_size ? `pack-input-${idx}` : `qty-input-${idx}`} />
                                            </div>
                                        </td>
                                        <td className="px-2 py-3">
                                            {l.pack_size ? (
                                                <input type="number" value={l.quantity || ''} onChange={e => updateLine(idx, 'content_total', e.target.value === '' ? 0 : parseInt(e.target.value))} min="0"
                                                    className="w-14 h-8 text-center text-sm font-semibold border border-[#E5E7EB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{borderRadius:'4px'}} data-testid={`content-input-${idx}`} />
                                            ) : <span className="text-xs text-[#D1D5DB] block text-center">-</span>}
                                        </td>
                                    </>
                                ) : (
                                    <td className="px-2 py-3">
                                        <div className="flex items-center justify-center gap-1">
                                            <button type="button" onClick={() => l.quantity > 1 && updateLine(idx, 'quantity', l.quantity - 1)} className="w-7 h-8 border border-[#E5E7EB] bg-white flex items-center justify-center hover:bg-[#F3F4F6] text-[#6B7280] transition-colors" style={{borderRadius:'4px'}} data-testid={`qty-minus-${idx}`}><Minus size={12} /></button>
                                            <input type="number" value={l.quantity || ''} onChange={e => updateLine(idx, 'quantity', e.target.value === '' ? 0 : parseInt(e.target.value))} min="0" className="w-12 h-8 text-center text-sm font-semibold border border-[#E5E7EB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{borderRadius:'4px'}} data-testid={`qty-input-${idx}`} />
                                            <button type="button" onClick={() => updateLine(idx, 'quantity', l.quantity + 1)} className="w-7 h-8 border border-[#E5E7EB] bg-white flex items-center justify-center hover:bg-[#F3F4F6] text-[#6B7280] transition-colors" style={{borderRadius:'4px'}} data-testid={`qty-plus-${idx}`}><Plus size={12} /></button>
                                        </div>
                                    </td>
                                )}
                                <td className="px-2 py-3">
                                    <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF]">&euro;</span>
                                        <input type="number" value={l.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} step="0.01"
                                            className="w-full h-7 pl-5 pr-1 text-center text-sm font-medium border border-[#D1D5DB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`line-price-${idx}`} />
                                    </div>
                                </td>
                                <td className="px-2 py-3">
                                    <input type="number" value={l.discount_percent || ''} onChange={e => updateLine(idx, 'discount_percent', e.target.value)} min="0" max="100" placeholder="%"
                                        className="w-full h-7 text-center text-sm border border-[#D1D5DB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`line-disc-${idx}`} />
                                </td>
                                {hasDiscount && (
                                    <td className="px-2 py-3 text-right text-xs text-[#EF4444]">
                                        {discountAmt > 0 ? `-\u20AC${fmtDec(discountAmt)}` : ''}
                                    </td>
                                )}
                                <td className="px-4 py-3 text-right">
                                    <span className="text-sm font-bold text-[#111827]">&euro;{fmtDec(l.line_total)}</span>
                                </td>
                                <td className="pr-3 py-3">
                                    <button onClick={() => removeLine(idx)} className="w-7 h-7 flex items-center justify-center text-[#D1D5DB] hover:text-[#EF4444] hover:bg-[#FEF2F2] transition-colors" data-testid={`remove-line-${idx}`}>
                                        <Trash2 size={15} />
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
                    );
                })()}
            </div>

            {/* FOOTER */}
            {lines.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="order-footer">
                    <div className="grid grid-cols-2 gap-8 mb-4">
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Miktar Bilgileri</h4>
                            <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Kalem</span><span className="font-semibold text-[#111827]" data-testid="order-line-count">{totals.lineCount}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Toplam Adet</span><span className="font-semibold text-[#111827]" data-testid="order-total-pieces">{totals.sumPieces}</span></div>
                            {totals.hasPackProducts && (<>
                                <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Toplam Paket</span><span className="font-semibold text-[#111827]" data-testid="order-total-packs">{totals.sumPacks}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Paket Ici Toplam</span><span className="font-semibold text-[#111827]" data-testid="order-pack-content-total">{totals.packContentTotal}</span></div>
                            </>)}
                            {totals.totalPallets > 0 && <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Toplam Palet</span><span className="font-semibold text-[#111827]" data-testid="order-total-pallets">{totals.totalPallets}</span></div>}
                            {totals.totalWeight > 0 && <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Toplam Agirlik</span><span className="font-semibold text-[#111827]">{totals.totalWeight.toLocaleString('de-DE', { minimumFractionDigits: 1 })} kg</span></div>}
                        </div>
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Fiyat Ozeti</h4>
                            <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Subtotal</span><span className="font-semibold text-[#111827]">&euro;{fmtDec(totals.subtotal)}</span></div>
                            {totals.lineDiscountTotal > 0 && <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Urun Indirimi</span><span className="font-semibold text-[#EF4444]">-&euro;{fmtDec(totals.lineDiscountTotal)}</span></div>}
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-[#6B7280]">Genel Indirim</span>
                                <div className="flex items-center gap-1.5">
                                    <input type="number" value={globalDiscount || ''} onChange={e => { setGlobalDiscount(e.target.value); setIsDirty(true); }} placeholder="0" min="0" max="100"
                                        className="w-12 h-7 text-center text-sm font-semibold border border-[#D1D5DB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{borderRadius:'4px'}} data-testid="global-discount-input" />
                                    <span className="text-xs text-[#9CA3AF]">%</span>
                                    {totals.discountAmount > 0 && <span className="text-[#EF4444] font-semibold text-xs" data-testid="discount-amount">-&euro;{fmtDec(totals.discountAmount)}</span>}
                                </div>
                            </div>
                            <div className="pt-2 mt-2 border-t border-[#F3F4F6] space-y-2">
                                {totals.totalPallets > 0 && !palletAlreadyAdded && (
                                    <div>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={palletEnabled} onChange={e => setPalletEnabled(e.target.checked)} className="rounded border-[#D1D5DB]" data-testid="pallet-checkbox" />
                                            <span className="text-sm text-[#374151] font-medium">Paletleri siparise ekle</span>
                                        </label>
                                        {palletEnabled && (
                                            <div className="flex items-center gap-2 mt-1.5 ml-6">
                                                <div className="relative flex-1">
                                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF]">&euro;</span>
                                                    <input type="number" value={palletPrice} onChange={e => setPalletPrice(e.target.value)} placeholder="Fiyat" step="0.01"
                                                        className="w-full h-8 pl-5 pr-2 text-sm border border-[#D1D5DB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{borderRadius:'4px'}} data-testid="pallet-price-input" />
                                                </div>
                                                <button type="button" onClick={addPalletLine}
                                                    className="h-8 px-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-xs font-semibold flex items-center gap-1 transition-colors" style={{borderRadius:'4px'}} data-testid="add-pallet-btn">
                                                    <Plus size={12} /> Ekle
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {palletAlreadyAdded && totals.totalPallets > 0 && (
                                    <div className="flex items-center gap-2 text-sm text-[#059669]"><Check size={14} /> <span className="font-medium">Paletler eklendi</span></div>
                                )}
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={showProfit} onChange={e => { setShowProfit(e.target.checked); setIsDirty(true); }} className="rounded border-[#D1D5DB]" data-testid="profit-checkbox" />
                                    <span className="text-sm text-[#374151] font-medium">Kar bilgisini goster</span>
                                </label>
                                {showProfit && (
                                    <div className="ml-6 flex items-baseline gap-2">
                                        {totals.hasPurchasePrice ? (
                                            <>
                                                <span className={`text-base font-bold ${totals.profit >= 0 ? 'text-[#059669]' : 'text-[#EF4444]'}`} data-testid="order-profit">&euro;{fmtDec(totals.profit)}</span>
                                                {totals.profitPercent !== 0 && <span className={`text-xs font-semibold ${totals.profit >= 0 ? 'text-[#059669]' : 'text-[#EF4444]'}`}>(%{totals.profitPercent.toFixed(1)})</span>}
                                            </>
                                        ) : (
                                            <span className="text-xs text-[#D97706]">Tum urunlerin alis fiyatini girin</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3">
                        <textarea value={orderNotes} onChange={e => { setOrderNotes(e.target.value); setIsDirty(true); }}
                            placeholder="Siparis notu ekleyin..." rows={2}
                            className="form-input w-full text-sm resize-none" data-testid="order-notes" />
                    </div>
                    <div className="border-t border-[#E5E7EB] pt-4 flex items-center justify-between">
                        <div>
                            <span className="text-sm text-[#6B7280] mr-2">Toplam</span>
                            <span className="text-2xl font-bold text-[#111827]" data-testid="order-grand-total">&euro;{fmtDec(totals.total)}</span>
                        </div>
                        <button onClick={handleSave} disabled={saving}
                            className="h-11 px-8 bg-[#374151] hover:bg-[#1F2937] text-white text-sm font-semibold flex items-center gap-2 transition-colors" style={{borderRadius:'4px'}} data-testid="save-order-btn">
                            <Check size={15} /> {saving ? 'Kaydediliyor...' : (isEditing ? 'Guncelle' : 'Kaydet')}
                        </button>
                    </div>
                </div>
            )}

            {/* VARIANT PICKER MODAL */}
            {variantPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setVariantPicker(null)} data-testid="variant-picker-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-label="Varyant secimi">
                        <div className="flex items-center justify-between p-5 border-b border-[#F3F4F6]">
                            <div>
                                <h3 className="font-bold text-[#111827]">{variantPicker.name}</h3>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">{variantPicker.reference || ''} &middot; Fiyat {customerPriceGroup || 'A'}: &euro;{fmtDec(getPriceForProduct(variantPicker, customerPriceGroup))}</p>
                            </div>
                            <button onClick={() => setVariantPicker(null)} className="w-8 h-8 rounded-lg hover:bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] transition-colors" data-testid="close-variant-picker">
                                <span className="text-xl leading-none">&times;</span>
                            </button>
                        </div>
                        <div className="p-4 max-h-[350px] overflow-y-auto space-y-2">
                            {variantPicker.variants?.map(v => (
                                <div key={v.id} className="flex items-center justify-between p-3 rounded-xl border border-[#F3F4F6] hover:border-[#D1D5DB] transition-colors" data-testid={`variant-pick-${v.id}`}>
                                    <div className="flex-1">
                                        <div className="text-sm font-semibold text-[#111827]">{v.name}</div>
                                        <div className="text-[11px] text-[#9CA3AF]">{v.sku || v.barcode || ''} &middot; Stok: {v.current_stock || 0}</div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button type="button" onClick={() => setVariantQtys(p => ({ ...p, [v.id]: Math.max(0, (p[v.id] || 0) - 1) }))}
                                            className="w-7 h-7 border border-[#E5E7EB] bg-white flex items-center justify-center hover:bg-[#F3F4F6] text-[#6B7280] transition-colors" style={{borderRadius:'4px'}} data-testid={`variant-qty-minus-${v.id}`}>
                                            <Minus size={12} />
                                        </button>
                                        <input type="number" min="0" value={variantQtys[v.id] === 0 ? '' : (variantQtys[v.id] || '')}
                                            onChange={e => setVariantQtys(p => ({ ...p, [v.id]: e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0) }))}
                                            className="w-12 h-7 text-center text-sm font-semibold border border-[#E5E7EB] bg-white focus:outline-none focus:ring-1 focus:ring-[#3B82F6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" style={{borderRadius:'4px'}} data-testid={`variant-qty-${v.id}`} />
                                        <button type="button" onClick={() => setVariantQtys(p => ({ ...p, [v.id]: (p[v.id] || 0) + 1 }))}
                                            className="w-7 h-7 border border-[#E5E7EB] bg-white flex items-center justify-center hover:bg-[#F3F4F6] text-[#6B7280] transition-colors" style={{borderRadius:'4px'}} data-testid={`variant-qty-plus-${v.id}`}>
                                            <Plus size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-[#F3F4F6] flex gap-3">
                            <button onClick={() => setVariantPicker(null)} className="flex-1 h-10 rounded-xl border border-[#D1D5DB] text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] transition-colors">Iptal</button>
                            <button onClick={confirmVariantPicker} className="flex-1 h-10 rounded-xl bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-semibold transition-colors" data-testid="confirm-variants-btn">Ekle</button>
                        </div>
                    </div>
                </div>
            )}

            {/* BARCODE SCANNER MODAL */}
            {showScanner && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={closeScanner}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-label="Barkod tarama">
                        <div className="flex items-center justify-between p-4 border-b border-[#F3F4F6]">
                            <h3 className="font-bold text-[#111827]">Barkod Tara</h3>
                            <button onClick={closeScanner} className="w-8 h-8 rounded-lg hover:bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF]" data-testid="close-scanner-btn">
                                <span className="text-xl leading-none">&times;</span>
                            </button>
                        </div>
                        <div className="p-4">
                            <div id="order-scanner-container" className="w-full rounded-xl overflow-hidden"></div>
                            {!scannerReady && <div className="text-center py-6 text-[#9CA3AF] text-sm">Kamera baslatiliyor...</div>}
                        </div>
                        <div className="p-4 border-t border-[#F3F4F6]">
                            <button onClick={closeScanner} className="w-full h-10 rounded-xl border border-[#D1D5DB] text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] transition-colors" data-testid="cancel-scanner-btn">Kapat</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
